import { FC, useMemo } from "react";
import { useParams } from "react-router";
import { TablePromptProps } from "./types";
import {
  selectActiveByDateModelsState,
  selectModelLoading,
  selectModelsLoadError,
} from "../../store/selectors/models";
import { selectAuthSubscriptionPlan } from "../../store/selectors/auth";
import { fetchModels } from "../../store/models/slice";
import { ERoutes } from "../../constants/routes";
import { selectScoresByPromptState } from "../../store/selectors/scores";
import { modelHasPromptTableScore } from "../../utils/modelHasPromptTableScore";
import { shouldShowModelInDailyList } from "../../utils/shouldShowModelInDailyList";
import { fetchScorePrompt } from "../../store/scores/slice";
import { fetchResponsesPrompt } from "../../store/response/slice";
import { useAppDispatch, useAppSelector } from "../../hooks";
import TableDaily from "../Table/TableDaily";
import { TableDailyConfig } from "../Table/types";
import { buildColumns, buildData } from "./utils/table";
import { Model } from "../../types/models";

const TablePrompt: FC<TablePromptProps> = ({ prompt, index }) => {
  const { date } = useParams();
  const dispatch = useAppDispatch();
  const modelsByDate = useAppSelector(selectActiveByDateModelsState(date));
  const subscriptionPlan = useAppSelector(selectAuthSubscriptionPlan);
  const scoresByPrompt = useAppSelector(selectScoresByPromptState);
  const promptScores = scoresByPrompt?.[String(prompt.id)] ?? null;
  const modelsLoading = useAppSelector(selectModelLoading);
  const modelsError = useAppSelector(selectModelsLoadError);

  const models = useMemo(() => {
    if (!modelsByDate) return null;

    return modelsByDate.filter((model) => {
      const hasScoreData = modelHasPromptTableScore(promptScores, model.id);
      return shouldShowModelInDailyList(model, hasScoreData, subscriptionPlan);
    });
  }, [modelsByDate, promptScores, subscriptionPlan]);

  const config: TableDailyConfig<string> = useMemo(() => ({
    items: models,
    loading: modelsLoading,
    error: modelsError,
    onRetry: () => dispatch(fetchModels()),
    scoresSelector: selectScoresByPromptState,
    buildColumns,
    buildData: (params) => {
      const normalizedModels = (params.items as Model[]).map((m) => ({
        id: Number(m.id),
        name: m.name,
        data: m.data,
      }));
      return buildData({
        models: normalizedModels,
        modelsDailyBaseRoute: params.baseRoute,
        selectBrands: params.selectBrands,
        scorePrompt: params.score,
        isScoreLoading: params.isScoreLoading,
      });
    },
    fetchScore: ({ date, entityId, brandIds }) => {
      dispatch(fetchScorePrompt({ 
        date, 
        prompt_id: Number(entityId), 
        brand_ids: brandIds 
      }));
    },
    fetchResponses: ({ date, entityId, brandIds }) => {
      dispatch(fetchResponsesPrompt({ 
        date, 
        prompt_id: entityId, 
        brand_ids: brandIds 
      }));
    },
    baseRoute: ERoutes.ModelsDayly,
    navigateRoute: ERoutes.PromptsDayly,
    skeletonRows: 6,
  }), [models, modelsLoading, modelsError, dispatch, prompt.id]);

  return <TableDaily entityId={prompt.id} index={index} config={config} />;
};

export default TablePrompt;
